// @ts-check
/**
 * ADM-02: things are created and edited on their own page, not in a
 * disclosure or a modal. See docs/reference/admin-pages.md.
 *
 *   const create = createFormPage({
 *     name: "new-automation",              // router page (router.js `pages`)
 *     list: "#/automations/schedules",     // Back, Cancel and success return here
 *     backText: "Automations",
 *     title: "New automation",
 *     description: "…",
 *     submitText: "Create automation",
 *     allowed: () => canManage,
 *     onSubmit: async () => { await client.create(…); },
 *   });
 *   create.fields.append(field({ label: "Name", control }));
 *   header actions: button({ text: "New automation", icon: "plus",
 *     variant: "primary", onClick: () => create.open() })
 *
 * A page for one record (`#/<area>/:id`) uses `createSubPage`, which has the
 * same header and Back link but no form; `onShow(route)` receives `route.id`.
 */
import { createManagementPage, focusPageHeading } from "./management-page.js";
import { router } from "./router.js";
import { button, setBusy } from "./ui/button.js";
import { h } from "./ui/dom.js";
import { banner } from "./ui/feedback.js";
import { form as formElement, validate } from "./ui/field.js";
import { pageBody, pageHeader } from "./ui/page.js";

/**
 * @typedef {{
 *   name: string, list: string | (() => string), backText: string, title: string,
 *   description?: string, allowed?: () => boolean,
 *   onShow?: (route: import("./router.js").Route) => void,
 *   onClose?: () => void, section?: string, parent?: Element,
 *   className?: string, actions?: Node[],
 * }} SubPageOptions
 */

/**
 * A full page under a list: page header with a Back link, and a body.
 * @param {SubPageOptions} options
 */
export function createSubPage({
  name,
  list,
  backText,
  title,
  description,
  allowed = () => true,
  onShow = () => {},
  onClose = () => {},
  section,
  parent,
  className = "",
  actions = [],
}) {
  const header = pageHeader({
    title,
    description,
    actions,
    back: { text: backText, onClick: () => controller.back() },
    // Settings has its own top bar and Sections button.
    level: section ? 2 : 1,
    menu: !section,
  });
  const titleElement = /** @type {HTMLElement} */ (
    header.querySelector(".ui-page-header__title")
  );
  // A record page names its record once loaded, so the description always exists.
  let description_ = /** @type {HTMLElement | null} */ (
    header.querySelector(".ui-page-header__description")
  );
  if (!description_) {
    description_ = h("p", { class: "ui-page-header__description" });
    description_.hidden = true;
    titleElement.parentElement?.append(description_);
  }
  const status = h("div", { class: "ui-form-page__status" });
  const body = pageBody({}, status);
  const page = h(
    "section",
    {
      class: `ui-sub-page${className ? ` ${className}` : ""}`,
      dataset: { page: name },
    },
    header,
    body,
  );
  const management = createManagementPage({
    page,
    name,
    allowed,
    section,
    parent,
    onShow: (route) => {
      notify();
      onShow(route);
    },
  });
  // The list hash the page was opened from in this tab, if any.
  let openedFrom = "";
  page.addEventListener("close", () => {
    openedFrom = "";
    notify();
    onClose();
  });
  /** @param {string} [text] @param {"info" | "success" | "warning" | "error"} [tone] */
  function notify(text = "", tone = "error") {
    status.replaceChildren(
      ...(text
        ? [banner({ tone, title: text, onDismiss: () => notify() })]
        : []),
    );
  }
  const controller = {
    page,
    header,
    body,
    /** Show an error or note above the page content; empty clears it. */
    notify,
    get visible() {
      return management.visible;
    },
    /** Open the page (`id` for a record page, `item` for a record under it). */
    open(/** @type {string} */ id = "", /** @type {string} */ item = "") {
      if (!management.visible) openedFrom = globalThis.location?.hash || "";
      management.open(undefined, { ...(id && { id }), ...(item && { item }) });
    },
    /** Change the title and description, e.g. to the record's name. */
    setTitle(/** @type {string} */ text, /** @type {string} */ detail) {
      titleElement.textContent = text;
      if (detail !== undefined) {
        description_.textContent = detail;
        description_.hidden = !detail;
      }
    },
    /**
     * Return to the list. When the page was opened from the list in this tab,
     * go back in history so Back and the browser's Back agree; a deep link
     * replaces its own entry with the list.
     */
    back() {
      if (!management.visible) return;
      const target = typeof list === "function" ? list() : list;
      if (isList(openedFrom, target) && globalThis.history?.length > 1)
        globalThis.history.back();
      else router.navigate(target, { replace: true });
    },
  };
  return controller;
}

/** Whether `from` is the list or one of its tabs (`#/automations` and `#/automations/schedules`). @param {string} from @param {string} list */
function isList(from, list) {
  return (
    Boolean(from) &&
    (from === list ||
      from.startsWith(`${list}/`) ||
      list.startsWith(`${from}/`))
  );
}

/**
 * A create (or edit) page: a sub page with one form, Cancel and a primary
 * submit button. `onSubmit` runs after in-app validation; when it resolves the
 * page returns to the list, when it throws its message shows on the page.
 * Return `false` to stay on the page (e.g. a preview step is missing).
 *
 * @param {SubPageOptions & {
 *   submitText: string, cancelText?: string,
 *   onSubmit: (form: HTMLFormElement) => Promise<boolean | void> | boolean | void,
 *   onSuccess?: () => void,
 * }} options
 */
export function createFormPage({
  submitText,
  cancelText = "Cancel",
  onSubmit,
  onSuccess = () => {},
  ...options
}) {
  const sub = createSubPage(options);
  const fields = h("fieldset", { class: "ui-form-fields" });
  const submit = button({
    text: submitText,
    variant: "primary",
    type: "submit",
  });
  const cancel = button({
    text: cancelText,
    variant: "secondary",
    onClick: () => sub.back(),
  });
  const actions = h("div", { class: "ui-form-page__actions" }, cancel, submit);
  const form = formElement({ class: "ui-form-page__form" }, fields, actions);
  sub.body.append(form);
  let busy = false;
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (busy || !validate(form)) return;
    busy = true;
    setBusy(submit, true);
    sub.notify();
    try {
      const result = await onSubmit(form);
      if (result === false || !sub.visible) return;
      sub.back();
      onSuccess();
    } catch (error) {
      sub.notify(
        /** @type {Error} */ (error)?.message || "Could not save. Try again.",
      );
    } finally {
      busy = false;
      setBusy(submit, false);
    }
  });
  return {
    ...sub,
    get visible() {
      return sub.visible;
    },
    form,
    /** Where fields go: `fields.append(field({ label, control }))`. */
    fields,
    /** The Cancel / submit row; extra buttons (a preview) go before Cancel. */
    actions,
    submit,
    /** Focus the page title again after the page re-renders. */
    focus() {
      focusPageHeading(sub.page);
    },
  };
}
